import { configureStore } from '@reduxjs/toolkit';
import { createWrapper } from 'next-redux-wrapper';
import createSagaMiddleware from 'redux-saga';
import {
  HttpTransportType,
  HubConnectionBuilder,
  JsonHubProtocol,
  LogLevel,
} from '@microsoft/signalr';
import axios from './sagas/axiosConfig';
import rootReducer from './slices';
import rootSaga from './sagas';
import { logout } from './slices/user';
import { FLIGHTS_HUB, HOTELS_HUB, TIPS_HUB, TOURS_HUB, webSocketEventName } from '../constants';
import onHotelsUpdated from './eventsSignalR/onHotelsUpdated';
import onTipsNotificationReceived from './eventsSignalR/onTipsNotificationReceived';
import onFlightsUpdated from './eventsSignalR/onFlightsUpdated';
import onToursUpdated from './eventsSignalR/onToursUpdated';
import tourConnectionKey from '../helpers/tourConnectionKey';

const makeStore = () => {
  let store;
  const sagaMiddleware = createSagaMiddleware({
    onError: (error) => {
      if (error.response && error.response.status === 401) {
        store.dispatch(logout());
      }
    },
  });

  const connections = {};

  const HOTEL_SEARCH_ACTION = 'hotel/hotelSearch';
  const INIT_HOTEL_TRIPSEARCH_ACTION = 'tripSearch/initHotelSearchRequest';
  const HOTEL_POLLING_TRIPSEARCH_ACTION = 'tripSearch/pollingHotelSearchRequest';
  const FLIGHT_SEARCH_ACTION = 'flight/flightSearch';
  const FLIGHT_POLLING_ACTION = 'flight/pollingFlightSearchRequest';
  const INIT_FLIGHT_SEARCH_ACTION = 'flight/initFlightSearchRequest';
  const TOUR_REMAINING_SEARCH_ACTION = 'tour/tourRemainingSearchRequest';
  const TOUR_REMAINING_SEARCH_STOP_ACTION = 'tour/tourRemainingSearchStop';

  const CRUISE_SEARCH_ACTION = 'tripSearch/cruiseSearchRequest';
  const MAX_REONNECTION = 2;
  let reconnectCount = 0;

  const startSignalRConnection = (_connection, requestId, previousRequestId, slot) =>
    _connection
      .start()
      .then(() => {
        console.info('SignalR Connected');
        reconnectCount = 0;
        if (requestId) _connection.invoke('JoinGroup', requestId);
        if (previousRequestId) _connection.invoke('RemoveGroup', previousRequestId);
      })
      .catch((err) => {
        console.error('SignalR Connection Error: ', err);
        reconnectCount += 1;
        if (reconnectCount <= MAX_REONNECTION) {
          setTimeout(() => {
            if (connections[slot] !== _connection) return;

            console.log('Reconnecting...');
            startSignalRConnection(_connection, requestId, previousRequestId, slot);
          }, 3000);
        }
      });

  const handleRemoveConnection = (hub) => {
    connections[hub] = null;
  };

  const signalRMiddleware = () => (next) => async (action) => {
    const doGetRemainingHotels =
      (action.type === HOTEL_SEARCH_ACTION || action.type === HOTEL_POLLING_TRIPSEARCH_ACTION) &&
      action.payload &&
      action.payload.doGetRemainingHotelsOnly;

    const doGetMoreFlights =
      (action.type === FLIGHT_SEARCH_ACTION || action.type === FLIGHT_POLLING_ACTION) &&
      action.payload &&
      action.payload.doHaveMoreFlights;

    const doGetRemainingTours = action.type === TOUR_REMAINING_SEARCH_ACTION;

    // ON SEARCH, STOP CONNECTION.
    if (
      action.type === INIT_HOTEL_TRIPSEARCH_ACTION &&
      connections[HOTELS_HUB] &&
      connections[HOTELS_HUB].stop
    ) {
      connections[HOTELS_HUB].stop().then(() => {
        console.log('New hotel search - connection closed');
        connections[HOTELS_HUB] = null;
      });
    }

    if (
      action.type === INIT_FLIGHT_SEARCH_ACTION &&
      connections[FLIGHTS_HUB] &&
      connections[FLIGHTS_HUB].stop
    ) {
      connections[FLIGHTS_HUB].stop().then(() => {
        console.log('New flight search - connection closed');
        connections[FLIGHTS_HUB] = null;
      });
    }

    if (doGetRemainingTours || action.type === TOUR_REMAINING_SEARCH_STOP_ACTION) {
      const tourSlot = tourConnectionKey(action.payload && action.payload.tripId);
      const previousToursConnection = connections[tourSlot];
      if (previousToursConnection && previousToursConnection.stop) {
        connections[tourSlot] = null;
        previousToursConnection.stop();
      }
    }

    if (
      axios.defaults &&
      action &&
      (((!action.payload || !action.payload.isChangeCruisePage) &&
        action.type === CRUISE_SEARCH_ACTION) ||
        doGetRemainingHotels ||
        doGetMoreFlights ||
        doGetRemainingTours)
    ) {
      let hub = action.type === CRUISE_SEARCH_ACTION ? TIPS_HUB : HOTELS_HUB;
      if (action.type === FLIGHT_POLLING_ACTION) hub = FLIGHTS_HUB;
      if (doGetRemainingTours) hub = TOURS_HUB;
      // The tours hub holds one connection per catalogue load; the other hubs keep their single slot.
      const slot = doGetRemainingTours ? tourConnectionKey(action.payload.tripId) : hub;
      let connectionHub = axios.defaults.baseURL;
      connectionHub += hub;
      const protocol = new JsonHubProtocol();

      // oxlint-disable-next-line no-bitwise
      const transport = HttpTransportType.WebSockets;

      const options = {
        transport,
        logMessageContent: false,
        logger: LogLevel.Trace,
        skipNegotiation: true,
        // accessTokenFactory:
      };

      const connection = new HubConnectionBuilder()
        .withUrl(connectionHub, options)
        .withHubProtocol(protocol)
        .build();
      connections[slot] = connection;

      const requestId = action.payload && action.payload.requestId;
      const previousRequestId = action.payload && action.payload.previousRequestId;

      connections[slot].on(webSocketEventName.SendTips, onTipsNotificationReceived(store));
      connections[slot].on(
        webSocketEventName.HotelsUpdated,
        onHotelsUpdated(store, connections, handleRemoveConnection),
      );
      connections[slot].on(
        webSocketEventName.FlightsUpdated,
        onFlightsUpdated(store, connections, handleRemoveConnection),
      );
      connections[slot].on(
        webSocketEventName.ToursUpdated,
        onToursUpdated(store, connections, handleRemoveConnection),
      );

      connections[slot].onclose(() =>
        setTimeout(() => {
          console.log('onclose event', connections[slot]);
          if (connections[slot] === connection) {
            startSignalRConnection(connection, requestId, undefined, slot);
          }
        }, 5000),
      );

      startSignalRConnection(connections[slot], requestId, previousRequestId, slot);
      console.log('Start connection', connections[slot], requestId);
    }
    return next(action);
  };

  store = configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          ignoredActions: ['trip/bookTripRequest'],
          ignoredActionPaths: ['payload.tokenizeFunc'],
        },
      })
        .concat(sagaMiddleware)
        .concat(signalRMiddleware),
    devTools: true,
  });
  sagaMiddleware.run(rootSaga);

  return store;
};

const wrapper = createWrapper(makeStore, { debug: true });

export default wrapper;
